import React, { useEffect, useState } from "react";
import { Modal, Spinner, Alert } from "react-bootstrap";
import { apiClient } from "../../../api/client";
import { notifyError, notifySuccess } from "../../../components/AppToast";

/**
 * RegistrationQrModal — todas las formas de compartir la inscripción de la
 * academia en un solo lugar: copiar el enlace, y ver/descargar/imprimir el
 * QR (con el logo de la academia al centro si cargó uno).
 *
 * El PNG del QR ya viene compuesto desde el backend (GET
 * /companies/me/qr-registro/, ver apps/companies/services.py) — el logo vive
 * en S3 y componerlo ahí evita que el navegador necesite leer píxeles de una
 * imagen cross-origin (CORS). Se pide como blob (no un <img src> directo a la
 * URL de la API) porque el endpoint requiere el header Authorization que solo
 * `apiClient` adjunta.
 */
function RegistrationQrModal({ show, onHide, slug, registrationUrl }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient
      .get("/api/v1/companies/me/qr-registro/", { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        setImageUrl(URL.createObjectURL(res.data));
      })
      .catch(() => {
        if (cancelled) return;
        setError("No se pudo generar el código QR. Intenta de nuevo.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show]);

  // Libera el object URL al cerrar/desmontar — si no, cada apertura del
  // modal deja un blob vivo en memoria hasta recargar la página.
  useEffect(() => {
    if (!imageUrl) return undefined;
    return () => URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);

  function handleClose() {
    setImageUrl(null);
    setCopied(false);
    onHide();
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(registrationUrl);
      setCopied(true);
      notifySuccess("Enlace de inscripción copiado al portapapeles.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API puede fallar por permisos del navegador (Brave/Firefox
      // estrictos, contexto no seguro) — el enlace sigue visible arriba, así
      // que no es un callejón sin salida para el usuario.
      notifyError("No se pudo copiar automáticamente. Copia el enlace manualmente.");
    }
  }

  function handleDownload() {
    if (!imageUrl) return;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `qr-inscripcion-${slug || "academia"}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function handlePrint() {
    if (!imageUrl) return;
    // Iframe oculto en vez de una ventana nueva: no depende de que el
    // navegador permita pop-ups (que suelen bloquearse por defecto).
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) document.body.removeChild(iframe);
    };

    const doc = iframe.contentWindow?.document;
    if (!doc) {
      notifyError("No se pudo abrir el diálogo de impresión.");
      cleanup();
      return;
    }
    doc.open();
    doc.write(
      `<html><head><title>QR de inscripción</title></head>` +
        `<body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;">` +
        `<img id="qr-print-img" src="${imageUrl}" style="max-width:90%;max-height:90%;" />` +
        `</body></html>`
    );
    doc.close();

    const img = doc.getElementById("qr-print-img");
    const triggerPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };
    if (img.complete) triggerPrint();
    else img.onload = triggerPrint;
  }

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Compartir inscripción</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        <p className="text-muted small mb-3">
          Comparte el enlace o el código QR para que estudiantes nuevos se
          inscriban solos, sin que tengas que crear su cuenta.
        </p>
        {loading && (
          <div className="py-5">
            <Spinner animation="border" role="status" size="sm" className="me-2" />
            Generando código QR…
          </div>
        )}
        {!loading && error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && imageUrl && (
          <img
            src={imageUrl}
            alt="Código QR de inscripción de la academia"
            className="img-fluid border rounded"
            style={{ maxWidth: 280 }}
          />
        )}
        <p className="text-muted small text-break mt-3 mb-0">{registrationUrl}</p>
      </Modal.Body>
      <Modal.Footer className="justify-content-center flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={handleCopyLink}
        >
          <i className={`mdi ${copied ? "mdi-check" : "mdi-content-copy"} me-1`} aria-hidden="true" />
          {copied ? "¡Copiado!" : "Copiar enlace"}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={handleDownload}
          disabled={!imageUrl}
        >
          <i className="mdi mdi-download me-1" aria-hidden="true" />
          Descargar
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handlePrint}
          disabled={!imageUrl}
        >
          <i className="mdi mdi-printer me-1" aria-hidden="true" />
          Imprimir
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export { RegistrationQrModal };
